/**
 * MONT ROYAL — the park district on top of the drivable mountain. Owned by `world`.
 *
 * The terrain itself lives in `graph.height()` (closed form, see TUNING.terrain.montRoyal);
 * this file is only about what stands ON it:
 *
 *   · ZONING. A block is park when the mountain's own contribution at its centre clears
 *     `park.threshold`. Zoning off `mountainHeight()` rather than a radius means the park is
 *     whatever the terrain says is mountain — move the summit and the park follows, and the
 *     shape is a contour rather than a circle. Pure function of position, no rng (N3).
 *
 *   · FOREST instead of buildings. Park blocks emit no lots at all, which is both correct
 *     (there are no triplexes on Mont Royal) and the reason 27 extra blocks cost almost
 *     nothing: a forested block is a few dozen instanced trees against a Plateau block's ~20
 *     lots of staircases, balconies and window reveals.
 *
 *   · THE BELVÉDÈRE. A paved overlook on the summit intersection, facing east-south-east down
 *     over the Plateau and downtown. This is the phase's hero moment, so it gets real
 *     geometry: a plaza deck cut level into the slope, a low stone parapet along the view
 *     edge, and lamps and benches from the existing prop sets.
 *
 * Everything here samples `graph.walkY()`/`height()` for its Y, so it sits on the mountain
 * without knowing the mountain's shape — ARCHITECTURE.md §4.9c, which matters far more on a
 * 30 m hill than it did on flat ground.
 */

import { FACE, trs } from './geom.js';

/** Mountain contribution at a point — the zoning signal. */
export function parkHeight(graph, x, z) {
  return graph.mountainHeight(x, z);
}

/** True when a block sits high enough on the mountain to be parkland rather than city. */
export function isParkBlock(T, graph, block) {
  return graph.mountainHeight(block.cx, block.cz) >= T.park.threshold;
}

/**
 * Forest for one park block, into the existing tree PropSets.
 *
 * Trees are placed on a jittered lattice and rejected near the block edges so the carriageway
 * and sidewalk stay clear — the streets are cut into the forest, not laid over it. Density is
 * deliberately modest: `canopy*` has no far-LOD twin (unlike cones and balconies), so every
 * live tree is ~157 triangles at full price, and a forest is the one thing here that could
 * quietly cost more than the entire downtown.
 *
 * @param {Rng} rng  the block's own position-keyed fork (layout-key.js), so a block's trees
 *                   never move when another district is added
 */
export function plantForest(T, graph, block, rng, props) {
  const P = T.park;
  const step = P.treeStep;
  let n = 0;
  for (let x = block.x0 + P.treeEdge; x < block.x1 - P.treeEdge; x += step) {
    for (let z = block.z0 + P.treeEdge; z < block.z1 - P.treeEdge; z += step) {
      if (!rng.chance(P.treeP)) continue;
      const px = x + rng.spread(step * 0.42);
      const pz = z + rng.spread(step * 0.42);
      // Never plant on a road — the ruelles and the streets themselves cut through the park.
      const c = graph.classify(px, pz);
      if (c.surface === 'road' || c.surface === 'kerb') continue;
      const y = graph.height(px, pz) + graph.kerbH;
      const s = rng.range(P.treeScaleMin, P.treeScaleMax);
      const b = props.blockAt(px, pz);
      const spin = rng.range(0, 6.28);
      const crownYaw = rng.range(0, 6.28);
      props.sets.trunk.add(b, trs(px, y, pz, spin, s, s, s));
      props.contact('trunk', px, y, pz, spin, s, s);
      const crown = [px, y + 3.1 * s, pz, crownYaw, s, s * rng.range(0.85, 1.18), s];
      props.sets[rng.pick(props.canopySetNames)].add(b, trs(...crown));
      // Winter dress at the same pivot; exactly one of the two is ever visible.
      props.sets.branch.add(b, trs(...crown));
      props.sets.branchSnow.add(b, trs(...crown));
      n++;
    }
  }
  return n;
}

/**
 * THE BELVÉDÈRE. A level plaza on the summit intersection's corner, looking down over the city.
 *
 * The deck is cut LEVEL at the summit road height — a lookout that followed the terrain would
 * be a sloped shelf, and every real belvédère is a flat platform held up on its downhill side.
 * The parapet runs along the two view edges only; the uphill edges meet the ground.
 *
 * @returns {{x:number, z:number, y:number, w:number, d:number}} the plaza record, so the
 *          harness and the report can name where the hero camera should stand.
 */
export function buildLookout(T, graph, mbDeck, mbWall, colliders, props, rng) {
  const L = T.park.lookout;
  const M = T.terrain.montRoyal;
  // Anchored to the summit itself, offset toward the view so it sits on the block corner
  // rather than in the middle of the junction.
  const cx = M.x + L.offX, cz = M.z + L.offZ;
  const y = graph.roadY(M.x, M.z) + L.lift;
  const hw = L.width * 0.5, hd = L.depth * 0.5;

  // Deck. `y - L.skirt` reaches well below the downhill lip so the platform never floats over
  // the slope it is standing on — the same problem a building on a grade has, solved the same
  // way (see LotBuilder's min-corner base).
  const deck = [cx - hw, cz - hd, cx + hw, cz - hd, cx + hw, cz + hd, cx - hw, cz + hd];
  mbDeck.prism(deck, y - L.skirt, y, false, L.deckUv);
  capQuad(mbDeck, cx - hw, cz - hd, cx + hw, cz + hd, y);

  // Parapet along the two downhill (view) edges: +X faces the city, +Z faces downtown.
  const t = L.parapetT, ph = L.parapetH;
  mbWall.prism([cx + hw - t, cz - hd, cx + hw, cz - hd, cx + hw, cz + hd, cx + hw - t, cz + hd],
    y, y + ph, false, 0.5);
  capQuad(mbWall, cx + hw - t, cz - hd, cx + hw, cz + hd, y + ph);
  mbWall.prism([cx - hw, cz + hd - t, cx + hw - t, cz + hd - t, cx + hw - t, cz + hd, cx - hw, cz + hd],
    y, y + ph, false, 0.5);
  capQuad(mbWall, cx - hw, cz + hd - t, cx + hw - t, cz + hd, y + ph);

  // The parapet is a real collider — the player must not drive off the lookout.
  colliders.push({ minX: cx + hw - t, minZ: cz - hd, maxX: cx + hw, maxZ: cz + hd,
    y0: y - 2, top: y + ph, kind: 'building' });
  colliders.push({ minX: cx - hw, minZ: cz + hd - t, maxX: cx + hw, maxZ: cz + hd,
    y0: y - 2, top: y + ph, kind: 'building' });

  // Lamps and benches from the existing sets — zero new draw calls.
  if (props) {
    for (let i = 0; i < L.lamps; i++) {
      const t2 = (i + 0.5) / L.lamps;
      const lx = cx - hw + L.width * t2;
      const lz = cz + hd - t - L.furnitureIn;
      props.add('lampO', lx, y, lz, 0, 1, 1, 1, 0x9a9083);
    }
    for (let i = 0; i < L.benches; i++) {
      const t2 = (i + 0.5) / L.benches;
      const bz = cz - hd + L.depth * t2;
      props.add('benchIron', cx + hw - t - L.furnitureIn, y, bz, Math.PI * 0.5, 1, 1, 1, 0x4a4f52);
    }
  }
  void rng;
  return { x: cx, z: cz, y, w: L.width, d: L.depth };
}

/**
 * Upward-facing horizontal quad.
 *
 * `MeshBuilder.prism(cap = true)` winds its cap DOWNWARD for the ring order used throughout
 * this codebase, which is invisible on the double-sided masonry materials but not on the
 * single-sided ones — and a lookout deck you can see through is worse than one you cannot see
 * the underside of. Explicit winding, taken from `CUBE_FACES`' +Y face.
 */
function capQuad(mb, minX, minZ, maxX, maxZ, y) {
  mb.quad([minX, y, maxZ], [maxX, y, maxZ], [maxX, y, minZ], [minX, y, minZ]);
}

export { FACE };

/**
 * THE VISTA. Always-resident low-poly shells for every built block, so the city is still there
 * when you look down on it from the mountain.
 *
 * WHY IT IS NEEDED. Sector detail is distance-culled at `stream.sectorRadius` (~427 m with a
 * sector's own radius). From the belvédère at 30 m the player can see the whole 832 × 610 m
 * city, and almost all of it is past that radius — without this the payoff shot is an empty
 * plain with a few near blocks on it.
 *
 * WHY IT IS AFFORDABLE. One open prism per block over the block's own built envelope, at the
 * block's REAL built height (recorded by LotBuilder, not estimated), so the silhouette matches
 * exactly at the swap. Ten triangles a block; the whole city's vista layer is about 1,100
 * triangles. Park blocks are skipped — a forest has no silhouette worth a box, and the trees
 * themselves are block-streamed.
 *
 * EXCLUSIVITY is the same contract the downtown skylineShell learned the hard way: shells are
 * grouped BY SECTOR and `world.update()` shows a sector's shell only while that sector's detail
 * group is hidden. Exactly one of the two ever draws, so there is no depth interaction to get
 * wrong — see the note in downtown.js buildSkylineShells for what happens when you rely on an
 * inset instead.
 */
export function buildVistaShells(T, graph, blocks, mbFor) {
  const V = T.vista;
  let n = 0;
  for (const b of blocks) {
    if (b.isPark) continue;
    const top = b.builtTop;
    if (!(top > 0)) continue;
    const mb = mbFor(b.sector);
    if (!mb) continue;
    // The built envelope is the block inset by its frontage setback, shrunk a little more so
    // the shell is inside the real facades at the swap rather than outside them.
    const i = V.inset;
    const x0 = b.x0 + i, x1 = b.x1 - i, z0 = b.z0 + i, z1 = b.z1 - i;
    if (x1 - x0 < 2 || z1 - z0 < 2) continue;
    const gy = graph.walkY(b.cx, b.cz);
    mb.prism([x0, z0, x1, z0, x1, z1, x0, z1], gy - V.skirt, top - V.drop, false, V.uv);
    capQuad(mb, x0, z0, x1, z1, top - V.drop);
    n++;
  }
  return n;
}
